import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    MenuItem,
    Box,
    List,
    ListItem,
    ListItemText,
    IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { addOpeningHoursList } from '../../../lib/apiClientOpeningHours';
import ConfirmAddDialog from './ConfirmAddDialog';
import { useTranslation } from "react-i18next";

const AddOpeningHoursDialog = ({ open, onClose, businessId, onSuccess }) => {
    const { t } = useTranslation("addOpeningHoursDialog");

    const weekdayOptions = [
        { value: 0, label: t("weekdayNames.0") },
        { value: 1, label: t("weekdayNames.1") },
        { value: 2, label: t("weekdayNames.2") },
        { value: 3, label: t("weekdayNames.3") },
        { value: 4, label: t("weekdayNames.4") },
        { value: 5, label: t("weekdayNames.5") },
        { value: 6, label: t("weekdayNames.6") }
    ];

    const [validFrom, setValidFrom] = useState("");
    const [validTo, setValidTo] = useState("");
    const [weekday, setWeekday] = useState("");
    const [openTime, setOpenTime] = useState("");
    const [closeTime, setCloseTime] = useState("");
    const [minAdvance, setMinAdvance] = useState(0);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const [list, setList] = useState([]);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        if (!open) {
            setSuccess("");
            setError("");
            setList([]);
            setValidFrom("");
            setValidTo("");
            setWeekday("");
            setOpenTime("");
            setCloseTime("");
            setMinAdvance(0);
        }
    }, [open]);

    const getWeekdaysInRange = (from, to) => {
        const validDays = new Set();
        const start = new Date(from);
        const end = new Date(to);

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            validDays.add(d.getDay());
        }
        return validDays;
    };

    const weekdayMismatch = useMemo(() => {
        if (!validFrom || !validTo || weekday === "") return false;
        const daysInRange = getWeekdaysInRange(validFrom, validTo);
        return !daysInRange.has(Number(weekday));
    }, [validFrom, validTo, weekday]);

    const toTimeSpan = (t) => {
        if (!t) return null;
        return t.length === 5 ? `${t}:00` : t;
    };

    const handleAddItem = () => {
        if (!validFrom || !validTo || weekday === "" || !openTime || !closeTime) {
            setError(t("allFieldsRequired"));
            return;
        }

        const daysInRange = getWeekdaysInRange(validFrom, validTo);
        if (!daysInRange.has(Number(weekday))) {
            setError(t("weekdayNotInRange"));
            return;
        }

        const item = {
            validFrom,
            validTo,
            weekDay: Number(weekday),
            openTime: toTimeSpan(openTime),
            closeTime: toTimeSpan(closeTime),
            minimumAdvanceBookingMinutes: Number(minAdvance),
        };

        setList([...list, item]);
        setError("");
        setSuccess("");

        setWeekday("");
        setOpenTime("");
        setCloseTime("");
        setMinAdvance(0);
    };

    const handleDelete = (index) => {
        setList(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        try {
            await addOpeningHoursList(businessId, list);
            setSuccess(t("savedSuccess"));
            setError("");
            setList([]);

            if (onSuccess) onSuccess();
        } catch (err) {
            const msg = err.message?.toLowerCase() || "";

            if (msg.includes("overlap")) {
                setError(t("overlapError"));
                setSuccess("");
                return;
            }

            setError(t("saveFailed"));
            setSuccess("");
        }
    };

    const isDateRangeInvalid =
        validFrom && validTo && new Date(validTo) < new Date(validFrom);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
                {t("title")}
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {error && <Box color="red" mb={2}>{error}</Box>}
                {success && (
                    <Box color="green" textAlign="center" mb={2} fontWeight="bold">
                        {success}
                    </Box>
                )}

                <Box display="flex" gap={2} mb={2}>
                    <TextField
                        label={t("validFrom")}
                        fullWidth
                        type="date"
                        value={validFrom}
                        onChange={(e) => setValidFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label={t("validTo")}
                        fullWidth
                        type="date"
                        value={validTo}
                        onChange={(e) => setValidTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                {isDateRangeInvalid && (
                    <Box color="red" mb={2} fontWeight="bold">
                        {t("dateRangeInvalid")}
                    </Box>
                )}

                <TextField
                    fullWidth
                    select
                    label={t("weekday")}
                    value={weekday}
                    onChange={(e) => setWeekday(e.target.value)}
                    sx={{ mb: 2 }}
                >
                    {weekdayOptions.map((w) => (
                        <MenuItem key={w.value} value={w.value}>
                            {w.label}
                        </MenuItem>
                    ))}
                </TextField>

                {weekdayMismatch && (
                    <Box color="red" mb={2} fontWeight="bold">
                        {t("weekdayMismatch")}
                    </Box>
                )}

                <Box display="flex" gap={2} mb={2}>
                    <TextField
                        fullWidth
                        type="time"
                        label={t("openTime")}
                        value={openTime}
                        onChange={(e) => setOpenTime(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        fullWidth
                        type="time"
                        label={t("closeTime")}
                        value={closeTime}
                        onChange={(e) => setCloseTime(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                <TextField
                    fullWidth
                    label={t("minAdvance")}
                    type="number"
                    value={minAdvance}
                    onChange={(e) => setMinAdvance(e.target.value)}
                />

                <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={handleAddItem}
                    disabled={isDateRangeInvalid || weekdayMismatch}
                >
                    {t("addToList")}
                </Button>

                <Box mt={3}>
                    <List>
                        {list.map((item, idx) => (
                            <ListItem
                                key={idx}
                                secondaryAction={
                                    <IconButton onClick={() => handleDelete(idx)}>
                                        <DeleteIcon />
                                    </IconButton>
                                }
                            >
                                <ListItemText
                                    primary={`${weekdayOptions[item.weekDay].label}: ${item.openTime} - ${item.closeTime}`}
                                    secondary={`${t("validFrom")}: ${item.validFrom} → ${item.validTo}`}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>

                {list.length > 0 && (
                    <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        sx={{ mt: 2 }}
                        onClick={() => setConfirmOpen(true)}
                    >
                        {t("saveOpeningHours")}
                    </Button>
                )}
            </DialogContent>

            <ConfirmAddDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleSubmit}
                items={list}
            />
        </Dialog>
    );
};

export default AddOpeningHoursDialog;
